/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    CardMedia,
    Button,
    TextField,
    Grid,
    Paper,
    Chip,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Slider,
    Divider,
    IconButton,
    Collapse,
    Skeleton,
    useMediaQuery,
    useTheme,
    Drawer,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Breadcrumbs,
    Link,
} from '@mui/material';
import {
    DirectionsCar,
    LocationOn,
    CalendarToday,
    AccessTime,
    Search,
    FilterList,
    ExpandMore,
    ExpandLess,
    CheckCircle,
    Close,
    TuneRounded,
    Home,
    Person,
    Luggage,
    AcUnit,
    Settings,
    LocalGasStation,
    Speed,
    Bluetooth,
    Usb,
    GpsFixed,
} from '@mui/icons-material';
import { parseLocalDate, formatDate } from '../utils/dateHelpers';

// ============================================================================
// TYPES
// ============================================================================

interface CarResult {
    id: string;
    name: string;
    category: string;
    company: string;
    companyLogo: string;
    image: string;
    passengers: number;
    bags: number;
    transmission: 'automatic' | 'manual';
    fuelType: string;
    pricePerDay: number;
    totalPrice: number;
    currency: string;
    features: string[];
    mileage: string;
    freeCancellation: boolean;
    pickupLocation: string;
    dropoffLocation: string;
    rating?: number;
    reviewCount?: number;
}

interface SearchFilters {
    priceRange: [number, number];
    categories: string[];
    companies: string[];
    transmission: string[];
    features: string[];
    freeCancellation: boolean;
}

type SortOption = 'recommended' | 'price_low' | 'price_high' | 'rating';

// ============================================================================
// CONSTANTS
// ============================================================================

const CAR_CATEGORIES = [
    { key: 'economy', label: 'Economy' },
    { key: 'compact', label: 'Compact' },
    { key: 'midsize', label: 'Midsize' },
    { key: 'fullsize', label: 'Full-size' },
    { key: 'suv', label: 'SUV' },
    { key: 'luxury', label: 'Luxury' },
    { key: 'minivan', label: 'Minivan' },
    { key: 'convertible', label: 'Convertible' },
];

const RENTAL_COMPANIES = [
    { key: 'enterprise', name: 'Enterprise' },
    { key: 'hertz', name: 'Hertz' },
    { key: 'avis', name: 'Avis' },
    { key: 'budget', name: 'Budget' },
    { key: 'national', name: 'National' },
    { key: 'alamo', name: 'Alamo' },
    { key: 'dollar', name: 'Dollar' },
    { key: 'thrifty', name: 'Thrifty' },
];

const CAR_FEATURES = [
    { key: 'ac', label: 'Air Conditioning', icon: <AcUnit /> },
    { key: 'bluetooth', label: 'Bluetooth', icon: <Bluetooth /> },
    { key: 'gps', label: 'GPS Navigation', icon: <GpsFixed /> },
    { key: 'usb', label: 'USB Charger', icon: <Usb /> },
];

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
    { value: 'recommended', label: 'Recommended' },
    { value: 'price_low', label: 'Price: Low to High' },
    { value: 'price_high', label: 'Price: High to Low' },
    { value: 'rating', label: 'Highest Rated' },
];

// ============================================================================
// MOCK DATA GENERATOR
// ============================================================================

const generateMockCars = (
    pickupLocation: string,
    dropoffLocation: string,
    pickupDate: string,
    dropoffDate: string
): CarResult[] => {
    const days = pickupDate && dropoffDate
        ? Math.ceil(((parseLocalDate(dropoffDate)?.getTime() ?? 0) - (parseLocalDate(pickupDate)?.getTime() ?? 0)) / (1000 * 60 * 60 * 24))
        : 1;

    const cars: CarResult[] = [
        {
            id: 'car-1',
            name: 'Toyota Corolla',
            category: 'compact',
            company: 'enterprise',
            companyLogo: 'https://logos-world.net/wp-content/uploads/2021/08/Enterprise-Logo.png',
            image: 'https://images.unsplash.com/photo-1623869675781-80aa31012a5a?w=400',
            passengers: 5,
            bags: 2,
            transmission: 'automatic',
            fuelType: 'Gasoline',
            pricePerDay: 45,
            totalPrice: 45 * days,
            currency: 'USD',
            features: ['ac', 'bluetooth', 'usb'],
            mileage: 'Unlimited',
            freeCancellation: true,
            pickupLocation,
            dropoffLocation,
            rating: 4.5,
            reviewCount: 234,
        },
        {
            id: 'car-2',
            name: 'Honda Civic',
            category: 'compact',
            company: 'hertz',
            companyLogo: 'https://logos-world.net/wp-content/uploads/2021/08/Hertz-Logo.png',
            image: 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=400',
            passengers: 5,
            bags: 2,
            transmission: 'automatic',
            fuelType: 'Gasoline',
            pricePerDay: 48,
            totalPrice: 48 * days,
            currency: 'USD',
            features: ['ac', 'bluetooth', 'usb', 'gps'],
            mileage: 'Unlimited',
            freeCancellation: true,
            pickupLocation,
            dropoffLocation,
            rating: 4.6,
            reviewCount: 189,
        },
        {
            id: 'car-3',
            name: 'Nissan Versa',
            category: 'economy',
            company: 'budget',
            companyLogo: 'https://logos-world.net/wp-content/uploads/2021/08/Budget-Logo.png',
            image: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=400',
            passengers: 5,
            bags: 1,
            transmission: 'automatic',
            fuelType: 'Gasoline',
            pricePerDay: 32,
            totalPrice: 32 * days,
            currency: 'USD',
            features: ['ac', 'bluetooth'],
            mileage: 'Unlimited',
            freeCancellation: true,
            pickupLocation,
            dropoffLocation,
            rating: 4.2,
            reviewCount: 156,
        },
        {
            id: 'car-4',
            name: 'Toyota Camry',
            category: 'midsize',
            company: 'avis',
            companyLogo: 'https://logos-world.net/wp-content/uploads/2021/08/Avis-Logo.png',
            image: 'https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?w=400',
            passengers: 5,
            bags: 3,
            transmission: 'automatic',
            fuelType: 'Gasoline',
            pricePerDay: 58,
            totalPrice: 58 * days,
            currency: 'USD',
            features: ['ac', 'bluetooth', 'usb', 'gps'],
            mileage: 'Unlimited',
            freeCancellation: true,
            pickupLocation,
            dropoffLocation,
            rating: 4.7,
            reviewCount: 312,
        },
        {
            id: 'car-5',
            name: 'Jeep Grand Cherokee',
            category: 'suv',
            company: 'national',
            companyLogo: 'https://logos-world.net/wp-content/uploads/2021/08/National-Car-Rental-Logo.png',
            image: 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=400',
            passengers: 5,
            bags: 4,
            transmission: 'automatic',
            fuelType: 'Gasoline',
            pricePerDay: 85,
            totalPrice: 85 * days,
            currency: 'USD',
            features: ['ac', 'bluetooth', 'usb', 'gps'],
            mileage: 'Unlimited',
            freeCancellation: true,
            pickupLocation,
            dropoffLocation,
            rating: 4.8,
            reviewCount: 267,
        },
        {
            id: 'car-6',
            name: 'Ford Explorer',
            category: 'suv',
            company: 'alamo',
            companyLogo: 'https://logos-world.net/wp-content/uploads/2021/08/Alamo-Logo.png',
            image: 'https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=400',
            passengers: 7,
            bags: 4,
            transmission: 'automatic',
            fuelType: 'Gasoline',
            pricePerDay: 95,
            totalPrice: 95 * days,
            currency: 'USD',
            features: ['ac', 'bluetooth', 'usb', 'gps'],
            mileage: 'Unlimited',
            freeCancellation: false,
            pickupLocation,
            dropoffLocation,
            rating: 4.6,
            reviewCount: 198,
        },
        {
            id: 'car-7',
            name: 'BMW 3 Series',
            category: 'luxury',
            company: 'hertz',
            companyLogo: 'https://logos-world.net/wp-content/uploads/2021/08/Hertz-Logo.png',
            image: 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=400',
            passengers: 5,
            bags: 2,
            transmission: 'automatic',
            fuelType: 'Gasoline',
            pricePerDay: 125,
            totalPrice: 125 * days,
            currency: 'USD',
            features: ['ac', 'bluetooth', 'usb', 'gps'],
            mileage: 'Unlimited',
            freeCancellation: true,
            pickupLocation,
            dropoffLocation,
            rating: 4.9,
            reviewCount: 145,
        },
        {
            id: 'car-8',
            name: 'Dodge Grand Caravan',
            category: 'minivan',
            company: 'enterprise',
            companyLogo: 'https://logos-world.net/wp-content/uploads/2021/08/Enterprise-Logo.png',
            image: 'https://images.unsplash.com/photo-1559416523-140ddc3d238c?w=400',
            passengers: 7,
            bags: 5,
            transmission: 'automatic',
            fuelType: 'Gasoline',
            pricePerDay: 75,
            totalPrice: 75 * days,
            currency: 'USD',
            features: ['ac', 'bluetooth', 'usb'],
            mileage: 'Unlimited',
            freeCancellation: true,
            pickupLocation,
            dropoffLocation,
            rating: 4.4,
            reviewCount: 178,
        },
        {
            id: 'car-9',
            name: 'Chevrolet Spark',
            category: 'economy',
            company: 'dollar',
            companyLogo: 'https://logos-world.net/wp-content/uploads/2021/08/Dollar-Rent-A-Car-Logo.png',
            image: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=400',
            passengers: 4,
            bags: 1,
            transmission: 'manual',
            fuelType: 'Gasoline',
            pricePerDay: 28,
            totalPrice: 28 * days,
            currency: 'USD',
            features: ['ac'],
            mileage: 'Unlimited',
            freeCancellation: false,
            pickupLocation,
            dropoffLocation,
            rating: 4.0,
            reviewCount: 89,
        },
        {
            id: 'car-10',
            name: 'Ford Mustang Convertible',
            category: 'convertible',
            company: 'avis',
            companyLogo: 'https://logos-world.net/wp-content/uploads/2021/08/Avis-Logo.png',
            image: 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=400',
            passengers: 4,
            bags: 2,
            transmission: 'automatic',
            fuelType: 'Gasoline',
            pricePerDay: 110,
            totalPrice: 110 * days,
            currency: 'USD',
            features: ['ac', 'bluetooth', 'usb'],
            mileage: '150 miles/day',
            freeCancellation: true,
            pickupLocation,
            dropoffLocation,
            rating: 4.8,
            reviewCount: 203,
        },
    ];

    return cars;
};

// ============================================================================
// CAR CARD COMPONENT
// ============================================================================

interface CarCardProps {
    car: CarResult;
    days: number;
    onSelect: (car: CarResult) => void;
}

const CarCard: React.FC<CarCardProps> = ({ car, days, onSelect }) => {
    const featureIcons: Record<string, React.ReactElement> = {
        ac: <AcUnit sx={{ fontSize: 16 }} />,
        bluetooth: <Bluetooth sx={{ fontSize: 16 }} />,
        gps: <GpsFixed sx={{ fontSize: 16 }} />,
        usb: <Usb sx={{ fontSize: 16 }} />,
    };

    return (
        <Card
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                mb: 2,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid #e0e0e0',
                transition: 'box-shadow 0.2s, border-color 0.2s',
                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderColor: '#2D6E6A' },
            }}
        >
            {/* Image */}
            <Box sx={{ position: 'relative', width: { xs: '100%', sm: 240 }, minWidth: { sm: 240 } }}>
                <CardMedia
                    component="img"
                    sx={{ height: { xs: 180, sm: '100%' }, minHeight: { sm: 200 }, objectFit: 'cover' }}
                    image={car.image}
                    alt={car.name}
                />
                <Chip
                    label={car.category}
                    size="small"
                    sx={{ position: 'absolute', top: 12, left: 12, bgcolor: 'white', textTransform: 'capitalize', fontWeight: 600 }}
                />
            </Box>

            {/* Content */}
            <CardContent sx={{ flex: 1, p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1, pr: 2 }}>
                        {/* Car name & company */}
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '1rem', sm: '1.1rem' }, mb: 0.5 }}>
                            {car.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            or similar • {car.company.charAt(0).toUpperCase() + car.company.slice(1)}
                        </Typography>

                        {/* Specs */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Person sx={{ fontSize: 18, color: '#666' }} />
                                <Typography variant="body2">{car.passengers}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Luggage sx={{ fontSize: 18, color: '#666' }} />
                                <Typography variant="body2">{car.bags} bag{car.bags > 1 ? 's' : ''}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Settings sx={{ fontSize: 18, color: '#666' }} />
                                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{car.transmission}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Speed sx={{ fontSize: 18, color: '#666' }} />
                                <Typography variant="body2">{car.mileage}</Typography>
                            </Box>
                        </Box>

                        {/* Features */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                            {car.features.map((feature) => (
                                <Chip
                                    key={feature}
                                    icon={featureIcons[feature]}
                                    label={feature.toUpperCase()}
                                    size="small"
                                    sx={{ height: 24, fontSize: '0.65rem' }}
                                />
                            ))}
                        </Box>

                        {/* Policies */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            {car.freeCancellation && (
                                <Typography variant="caption" sx={{ color: '#2E7D32', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CheckCircle sx={{ fontSize: 14 }} /> Free cancellation
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Price */}
                    <Box sx={{ textAlign: 'right', minWidth: { xs: 80, sm: 120 } }}>
                        {car.rating && (
                            <Chip
                                label={car.rating.toFixed(1)}
                                sx={{ bgcolor: '#2D6E6A', color: 'white', fontWeight: 700, fontSize: '0.8rem', height: 28, mb: 1 }}
                            />
                        )}
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#FF6B35' }}>
                            ${car.pricePerDay}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">per day</Typography>
                        {days > 1 && (
                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
                                ${car.totalPrice} total
                            </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" display="block">
                            {days} day{days > 1 ? 's' : ''}
                        </Typography>
                    </Box>
                </Box>

                {/* Book button */}
                <Box sx={{ mt: 'auto', pt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        onClick={() => onSelect(car)}
                        sx={{ bgcolor: '#2D6E6A', borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 4, '&:hover': { bgcolor: '#009688' } }}
                    >
                        Select
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};

// ============================================================================
// FILTER SIDEBAR COMPONENT
// ============================================================================

interface FilterSidebarProps {
    filters: SearchFilters;
    onChange: (filters: SearchFilters) => void;
    maxPrice: number;
    resultCount: number;
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({ filters, onChange, maxPrice, resultCount }) => {
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
        price: true,
        category: true,
        company: true,
        transmission: true,
        features: false,
    });

    const toggleSection = (section: string) => {
        setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    const updateFilter = <K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) => {
        onChange({ ...filters, [key]: value });
    };

    const toggleArrayFilter = (key: 'categories' | 'companies' | 'transmission' | 'features', value: string) => {
        const current = filters[key];
        const updated = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
        updateFilter(key, updated);
    };

    const FilterSection: React.FC<{ title: string; id: string; children: React.ReactNode }> = ({ title, id, children }) => (
        <Box sx={{ mb: 2 }}>
            <Box onClick={() => toggleSection(id)} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', py: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{title}</Typography>
                {expandedSections[id] ? <ExpandLess /> : <ExpandMore />}
            </Box>
            <Collapse in={expandedSections[id]}><Box sx={{ pt: 1 }}>{children}</Box></Collapse>
            <Divider sx={{ mt: 2 }} />
        </Box>
    );

    return (
        <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterList /> Filters
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{resultCount} cars found</Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Price */}
            <FilterSection title="Price per day" id="price">
                <Slider
                    value={filters.priceRange}
                    onChange={(_, value) => updateFilter('priceRange', value as [number, number])}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(v) => `$${v}`}
                    min={0}
                    max={maxPrice}
                    sx={{ color: '#2D6E6A' }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption">${filters.priceRange[0]}</Typography>
                    <Typography variant="caption">${filters.priceRange[1]}</Typography>
                </Box>
            </FilterSection>

            {/* Category */}
            <FilterSection title="Car Type" id="category">
                <FormGroup>
                    {CAR_CATEGORIES.map((cat) => (
                        <FormControlLabel
                            key={cat.key}
                            control={<Checkbox checked={filters.categories.includes(cat.key)} onChange={() => toggleArrayFilter('categories', cat.key)} sx={{ '&.Mui-checked': { color: '#2D6E6A' } }} size="small" />}
                            label={cat.label}
                        />
                    ))}
                </FormGroup>
            </FilterSection>

            {/* Company */}
            <FilterSection title="Rental Company" id="company">
                <FormGroup>
                    {RENTAL_COMPANIES.map((company) => (
                        <FormControlLabel
                            key={company.key}
                            control={<Checkbox checked={filters.companies.includes(company.key)} onChange={() => toggleArrayFilter('companies', company.key)} sx={{ '&.Mui-checked': { color: '#2D6E6A' } }} size="small" />}
                            label={company.name}
                        />
                    ))}
                </FormGroup>
            </FilterSection>

            {/* Transmission */}
            <FilterSection title="Transmission" id="transmission">
                <FormGroup>
                    <FormControlLabel
                        control={<Checkbox checked={filters.transmission.includes('automatic')} onChange={() => toggleArrayFilter('transmission', 'automatic')} sx={{ '&.Mui-checked': { color: '#2D6E6A' } }} size="small" />}
                        label="Automatic"
                    />
                    <FormControlLabel
                        control={<Checkbox checked={filters.transmission.includes('manual')} onChange={() => toggleArrayFilter('transmission', 'manual')} sx={{ '&.Mui-checked': { color: '#2D6E6A' } }} size="small" />}
                        label="Manual"
                    />
                </FormGroup>
            </FilterSection>

            {/* Features */}
            <FilterSection title="Features" id="features">
                <FormGroup>
                    {CAR_FEATURES.map((feature) => (
                        <FormControlLabel
                            key={feature.key}
                            control={<Checkbox checked={filters.features.includes(feature.key)} onChange={() => toggleArrayFilter('features', feature.key)} sx={{ '&.Mui-checked': { color: '#2D6E6A' } }} size="small" />}
                            label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>{feature.icon}{feature.label}</Box>}
                        />
                    ))}
                </FormGroup>
                <FormControlLabel
                    control={<Checkbox checked={filters.freeCancellation} onChange={(e) => updateFilter('freeCancellation', e.target.checked)} sx={{ '&.Mui-checked': { color: '#2D6E6A' } }} size="small" />}
                    label="Free cancellation"
                />
            </FilterSection>
        </Paper>
    );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const CarSearchResultsPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Parse URL params
    const initialPickupLocation = searchParams.get('pickupLocation') || '';
    const initialDropoffLocation = searchParams.get('dropoffLocation') || initialPickupLocation;
    const initialPickupDate = searchParams.get('pickupDate') || '';
    const initialPickupTime = searchParams.get('pickupTime') || '10:00';
    const initialDropoffDate = searchParams.get('dropoffDate') || '';
    const initialDropoffTime = searchParams.get('dropoffTime') || '10:00';

    // Search state
    const [pickupLocation, setPickupLocation] = useState(initialPickupLocation);
    const [dropoffLocation, setDropoffLocation] = useState(initialDropoffLocation);
    const [pickupDate, setPickupDate] = useState(initialPickupDate);
    const [dropoffDate, setDropoffDate] = useState(initialDropoffDate);

    // Results state
    const [loading, setLoading] = useState(true);
    const [cars, setCars] = useState<CarResult[]>([]);

    // Filter state
    const [filters, setFilters] = useState<SearchFilters>({
        priceRange: [0, 200],
        categories: [],
        companies: [],
        transmission: [],
        features: [],
        freeCancellation: false,
    });

    // Sort state
    const [sortBy, setSortBy] = useState<SortOption>('recommended');

    // Mobile filter drawer
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    // Calculate days
    const days = pickupDate && dropoffDate
        ? Math.max(1, Math.ceil(((parseLocalDate(dropoffDate)?.getTime() ?? 0) - (parseLocalDate(pickupDate)?.getTime() ?? 0)) / (1000 * 60 * 60 * 24)))
        : 1;

    // Load results
    useEffect(() => {
        const loadResults = async () => {
            setLoading(true);
            await new Promise((resolve) => setTimeout(resolve, 1200));

            if (initialPickupLocation) {
                const mockCars = generateMockCars(initialPickupLocation, initialDropoffLocation, initialPickupDate, initialDropoffDate);
                setCars(mockCars);
                const maxPrice = Math.max(...mockCars.map((c) => c.pricePerDay), 200);
                setFilters((prev) => ({ ...prev, priceRange: [0, maxPrice] }));
            }
            setLoading(false);
        };
        loadResults();
    }, [initialPickupLocation, initialDropoffLocation, initialPickupDate, initialDropoffDate]);

    // Filter and sort
    const filteredCars = useMemo(() => {
        let result = [...cars];

        result = result.filter((car) => {
            if (car.pricePerDay < filters.priceRange[0] || car.pricePerDay > filters.priceRange[1]) return false;
            if (filters.categories.length > 0 && !filters.categories.includes(car.category)) return false;
            if (filters.companies.length > 0 && !filters.companies.includes(car.company)) return false;
            if (filters.transmission.length > 0 && !filters.transmission.includes(car.transmission)) return false;
            if (filters.features.length > 0 && !filters.features.every((f) => car.features.includes(f))) return false;
            if (filters.freeCancellation && !car.freeCancellation) return false;
            return true;
        });

        switch (sortBy) {
            case 'price_low': result.sort((a, b) => a.pricePerDay - b.pricePerDay); break;
            case 'price_high': result.sort((a, b) => b.pricePerDay - a.pricePerDay); break;
            case 'rating': result.sort((a, b) => (b.rating || 0) - (a.rating || 0)); break;
            default: result.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        }

        return result;
    }, [cars, filters, sortBy]);

    // Handle car selection
    const handleSelectCar = (car: CarResult) => {
        const params = new URLSearchParams({
            type: 'car',
            carId: car.id,
            carName: car.name,
            pickupLocation,
            dropoffLocation,
            pickupDate,
            dropoffDate,
            days: days.toString(),
            pricePerDay: car.pricePerDay.toString(),
            totalPrice: car.totalPrice.toString(),
        });
        navigate(`/book/checkout?${params.toString()}`);
    };

    const formatTime = (time: string) => {
        const [hour, minute] = time.split(':').map(Number);
        const h = hour % 12 || 12;
        const ampm = hour < 12 ? 'AM' : 'PM';
        return `${h}:${minute.toString().padStart(2, '0')} ${ampm}`;
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#F5F5F5' }}>
            {/* Header */}
            <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, zIndex: 100 }}>
                <Container maxWidth="xl">
                    <Box sx={{ py: 1 }}>
                        <Breadcrumbs sx={{ fontSize: '0.85rem' }}>
                            <Link component="button" onClick={() => navigate('/')} underline="hover" color="inherit" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Home sx={{ fontSize: 16 }} /> Home
                            </Link>
                            <Link component="button" onClick={() => navigate(-1)} underline="hover" color="inherit">Cars</Link>
                            <Typography color="text.primary" sx={{ fontSize: '0.85rem' }}>{pickupLocation}</Typography>
                        </Breadcrumbs>
                    </Box>

                    {/* Search Summary */}
                    <Box sx={{ py: 2 }}>
                        <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 2, border: '1px solid #e0e0e0', flexWrap: 'wrap', gap: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <LocationOn sx={{ color: '#2D6E6A' }} />
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{pickupLocation || 'Pick-up'}</Typography>
                                </Box>
                                <Divider orientation="vertical" flexItem />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarToday sx={{ fontSize: 18, color: '#666' }} />
                                    <Typography variant="body2">
                                        {pickupDate && formatDate(pickupDate, { month: 'short', day: 'numeric' })} {formatTime(initialPickupTime)}
                                        {' - '}
                                        {dropoffDate && formatDate(dropoffDate, { month: 'short', day: 'numeric' })} {formatTime(initialDropoffTime)}
                                    </Typography>
                                </Box>
                                <Divider orientation="vertical" flexItem />
                                <Typography variant="body2">{days} day{days > 1 ? 's' : ''}</Typography>
                            </Box>
                            <Button variant="contained" sx={{ bgcolor: '#2D6E6A', borderRadius: 2, textTransform: 'none', '&:hover': { bgcolor: '#009688' } }}>
                                Modify Search
                            </Button>
                        </Paper>
                    </Box>
                </Container>
            </Box>

            {/* Main Content */}
            <Container maxWidth="xl" sx={{ py: 3 }}>
                <Grid container spacing={3}>
                    {/* Filter Sidebar */}
                    {!isMobile && (
                        <Grid item md={3}>
                            <FilterSidebar filters={filters} onChange={setFilters} maxPrice={Math.max(...cars.map((c) => c.pricePerDay), 200)} resultCount={filteredCars.length} />
                        </Grid>
                    )}

                    {/* Results */}
                    <Grid item xs={12} md={9}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                {pickupLocation}: {filteredCars.length} cars available
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {isMobile && (
                                    <Button variant="outlined" startIcon={<TuneRounded />} onClick={() => setFilterDrawerOpen(true)} sx={{ borderRadius: 2, textTransform: 'none' }}>
                                        Filters
                                    </Button>
                                )}
                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                    <InputLabel>Sort by</InputLabel>
                                    <Select value={sortBy} label="Sort by" onChange={(e) => setSortBy(e.target.value as SortOption)} sx={{ borderRadius: 2 }}>
                                        {SORT_OPTIONS.map((opt) => (<MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>

                        {loading ? (
                            <Box>{[1, 2, 3].map((i) => (<Skeleton key={i} variant="rounded" height={200} sx={{ mb: 2, borderRadius: 2 }} />))}</Box>
                        ) : filteredCars.length === 0 ? (
                            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                                <DirectionsCar sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                                <Typography variant="h6" gutterBottom>No cars found</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Try adjusting your filters</Typography>
                            </Paper>
                        ) : (
                            <Box>{filteredCars.map((car) => (<CarCard key={car.id} car={car} days={days} onSelect={handleSelectCar} />))}</Box>
                        )}
                    </Grid>
                </Grid>
            </Container>

            {/* Mobile Filter Drawer */}
            <Drawer anchor="bottom" open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)} PaperProps={{ sx: { maxHeight: '85vh', borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}>
                <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>Filters</Typography>
                        <IconButton onClick={() => setFilterDrawerOpen(false)}><Close /></IconButton>
                    </Box>
                    <FilterSidebar filters={filters} onChange={setFilters} maxPrice={Math.max(...cars.map((c) => c.pricePerDay), 200)} resultCount={filteredCars.length} />
                    <Button fullWidth variant="contained" onClick={() => setFilterDrawerOpen(false)} sx={{ mt: 2, bgcolor: '#2D6E6A', borderRadius: 2, textTransform: 'none', '&:hover': { bgcolor: '#009688' } }}>
                        Show {filteredCars.length} Results
                    </Button>
                </Box>
            </Drawer>
        </Box>
    );
};

export default CarSearchResultsPage;
